import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ArrowLeft, X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef((
  { className, children, description, screen = false, "aria-describedby": ariaDescribedBy, ...props },
  ref
) => {
  // Radix warns in dev when a Dialog has no accessible description. Callers can
  // supply one via the `description` prop (rendered screen-reader-only below);
  // when neither a `description` nor an explicit `aria-describedby` is given we
  // opt out with aria-describedby={undefined} — Radix's sanctioned way to keep
  // the a11y tree valid and silence the warning. No file rendered its own
  // <DialogDescription>, so this default never breaks an existing association.
  const describedByProps =
    ariaDescribedBy !== undefined
      ? { "aria-describedby": ariaDescribedBy }
      : description
        ? {} // let Radix wire aria-describedby to the <DialogDescription> we render
        : { "aria-describedby": undefined };
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // max-h + overflow: this ERP's dialogs are mostly long forms, and without
          // a cap a phone-height viewport simply cuts the bottom off — including the
          // Save button — with no way to reach it.
          //
          // Below sm it is a bottom sheet: pinned to the bottom edge, full width,
          // rounded top, sliding up — where a thumb already is, and the shape
          // Android and iOS users expect a form to arrive in. The bottom padding
          // clears the home indicator. From sm up it is the centred dialog it
          // always was. Width stays an unprefixed `max-w-lg` (centred by mx-auto
          // on phones) because 50+ callers widen it with an unprefixed max-w-*,
          // and an `sm:` default would out-rank every one of them.
          "fixed inset-x-0 bottom-0 z-50 mx-auto grid max-h-[92dvh] w-full max-w-lg gap-4 overflow-y-auto rounded-t-2xl border bg-background p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
          "sm:inset-x-auto sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:mx-0 sm:max-h-[90dvh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:p-6 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
          // `screen`: on a phone the dialog becomes a full-screen native page —
          // ← app bar, scrolling body, sticky action bar (index.css, phone block).
          // From md up it is exactly the dialog above.
          screen ? "m3-dialog-screen" : "m3-kb-sheet",
          className
        )}
        {...describedByProps}
        {...props}>
        {screen ? (
          // Zero-height sticky holder, so the ← stays pinned over the app bar
          // however far the body scrolls. Phone only (index.css).
          <div className="m3-back-bar" aria-hidden={false}>
            <DialogPrimitive.Close className="m3-back" aria-label="Back">
              <ArrowLeft className="h-6 w-6" />
            </DialogPrimitive.Close>
          </div>
        ) : null}
        {description ? (
          <DialogDescription className="sr-only">{description}</DialogDescription>
        ) : null}
        {children}
        <DialogPrimitive.Close
          className="m3-dialog-x absolute right-3 top-3 rounded-sm p-1.5 opacity-70 sm:right-4 sm:top-4 sm:p-0 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    data-dialog-header=""
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    data-dialog-footer=""
    // Stacked, full-width buttons on a phone — a 36px-wide "Save" is a hard
    // target for a thumb. From sm up, the right-aligned row it always was.
    className={cn("flex flex-col-reverse gap-2 [&>button]:w-full sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2 sm:[&>button]:w-auto", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
